import type { IActionDefinitionDto, IActionDocDto } from "@kuika/kuika-cl-model";
import { summarizeActionDescription } from "./action-definition-summary";

/**
 * User-facing documentation for an action, read from `definition.doc` (the `Doc` block of
 * `Backend/CoreApi/Metadatas/Actions/*.json`, seeded from Kuika Academy). `description` is written
 * for the LLM tools; it is only the fallback here, cut down by `summarizeActionDescription`.
 */

/** The only host a docs link may point at — `doc.url` is data, and a link opens in a new tab. */
const ACADEMY_URL_PREFIX: string = "https://academy.kuika.com/";

type ActionDefinitionSource = IActionDefinitionDto | undefined | null;

function nonEmpty(text: string | undefined | null): string | undefined {
  const trimmed: string = typeof text === "string" ? text.trim() : "";
  return trimmed === "" ? undefined : trimmed;
}

function getDoc(definition: ActionDefinitionSource): IActionDocDto | undefined {
  return definition?.doc ?? undefined;
}

/** The one line the picker shows: the doc summary, else the description's first sentence. "" when neither. */
export function getActionDocSummary(definition: ActionDefinitionSource): string {
  const docSummary: string | undefined = nonEmpty(getDoc(definition)?.summary);
  if (docSummary) {
    return docSummary;
  }
  return summarizeActionDescription(definition?.description);
}

/**
 * The longer text behind the info icon. An undocumented action keeps today's behaviour — its full
 * description when the summary had to cut it. Never the same text as the visible summary, and never
 * anything when there is no summary to expand on.
 */
export function getActionDocDetails(definition: ActionDefinitionSource): string | undefined {
  const summary: string = getActionDocSummary(definition);
  if (summary === "") {
    return undefined;
  }
  const doc: IActionDocDto | undefined = getDoc(definition);
  const docDetails: string | undefined = nonEmpty(doc?.details);
  if (docDetails) {
    return docDetails === summary ? undefined : docDetails;
  }
  if (nonEmpty(doc?.summary)) {
    return undefined;
  }
  const description: string | undefined = nonEmpty(definition?.description);
  if (description && description.length > summary.length) {
    return description;
  }
  return undefined;
}

/** The Academy page for the action, or undefined when it has none (or points anywhere else). */
export function getActionDocUrl(definition: ActionDefinitionSource): string | undefined {
  const url: string | undefined = nonEmpty(getDoc(definition)?.url);
  if (url && url.startsWith(ACADEMY_URL_PREFIX)) {
    return url;
  }
  return undefined;
}

function readParameterDoc(parameters: Record<string, string>, key: string): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(parameters, key)) {
    return undefined;
  }
  const value: unknown = parameters[key];
  return typeof value === "string" ? nonEmpty(value) : undefined;
}

/**
 * A parameter's one-line description. Exact key first; then an ASCII case-insensitive match, since a
 * serializer may camel-case dictionary keys ("UserId" → "userId").
 */
export function getActionParameterDoc(definition: ActionDefinitionSource, parameterName: string): string | undefined {
  const parameters: Record<string, string> | undefined = getDoc(definition)?.parameters;
  if (!parameters || !parameterName) {
    return undefined;
  }
  const exact: string | undefined = readParameterDoc(parameters, parameterName);
  if (exact) {
    return exact;
  }
  const wanted: string = parameterName.toLowerCase();
  const key: string | undefined = Object.keys(parameters).find(
    (candidate: string): boolean => candidate.toLowerCase() === wanted
  );
  return key === undefined ? undefined : readParameterDoc(parameters, key);
}
